import { useMutation } from '@apollo/client'
import { FormikProps, withFormik } from 'formik'
// TODO: Fix this auto-generated suppression.
// eslint-disable-next-line rulesdir/require-next-link-wrapper
import Link from 'next/link'
import { useRouter } from 'next/router'
import * as React from 'react'
import * as Yup from 'yup'

import { Button, DeprecatedText, Spacer, TypeStyle } from '@syconium/little-miss-figgy'

import { reportClientError } from '../../../../app/_components/chrome/scripts/DataDogRumScript'
import { useTranslation } from '../../../../app/_providers/TranslationProvider.client'
import { FORGOT_PASSWORD } from '../../../../lib/graphql/mutations/accountServices/FORGOT_PASSWORD'
import { GrayHorizontalBar } from '../../../../lib/shared-styles'
import { FormInput } from '../formInput'
import { ButtonWrapper } from '../styles'

import { ForgotPasswordProps, IForgotPasswordResponse } from './IProps'
import { PasswordReset, Underline } from './styles'

type MyError = { message: string; status?: number; isPasswordResetError?: boolean }

const ForgotPasswordError: React.FC<{ error: MyError | null }> = ({ error }) => {
  const {
    magnolia: { general: strings },
  } = useTranslation()

  if (!error) return null

  if (error.isPasswordResetError) {
    return (
      <PasswordReset data-testid='password-reset-error' state='error'>
        <TypeStyle.BodyLarge>{error.message}</TypeStyle.BodyLarge>
      </PasswordReset>
    )
  }

  if (error.status === 404) {
    return (
      <PasswordReset data-testid='email-not-found' state='error'>
        <TypeStyle.BodyLarge>
          {strings.requestedEmailNotFound}{' '}
          <Link href='/account/register'>
            <Underline>{strings.here}</Underline>
          </Link>
        </TypeStyle.BodyLarge>
      </PasswordReset>
    )
  }

  return <PasswordReset data-testid='password-reset-error' state='error'></PasswordReset>
}

interface IProps {
  displayPasswordReset: (arg0: boolean) => void
  passwordResetSuccess: (arg0: boolean) => void
  passwordResetError?: string
}

const ForgotPasswordForm: React.FC<FormikProps<{ email: string }> & IProps> = props => {
  const {
    displayPasswordReset,
    passwordResetError,
    passwordResetSuccess,
    values: { email },
  } = props
  const {
    magnolia: { general: strings },
  } = useTranslation()
  const router = useRouter()
  const [forgotPassword] = useMutation<IForgotPasswordResponse, { email: string }>(FORGOT_PASSWORD)
  const [forgotPasswordError, setForgotPasswordError] = React.useState<MyError | null>(
    !!passwordResetError
      ? {
          isPasswordResetError: true,
          message: passwordResetError,
        }
      : null
  )

  const onSubmit = async (event: React.BaseSyntheticEvent) => {
    event.preventDefault()
    const response = await forgotPassword({ variables: { email } })

    // In this case something catastrophic/unknown is occurring
    if (!response || !response?.data) {
      reportClientError({
        error: new Error('[ERROR]: forgotPassword mutation failed'),
        context: {
          scope: 'ForgotPassword',
        },
      })
      setForgotPasswordError({ message: 'error', status: 400 })
      return
    }

    if (response.data.forgotPassword?.errors?.length) {
      const error = response.data.forgotPassword.errors[0]!
      if (error.status !== 404) {
        reportClientError({
          error,
          context: {
            scope: 'ForgotPassword',
          },
        })
      }
      setForgotPasswordError(error)
      return
    }

    setForgotPasswordError(null)
    passwordResetSuccess(true)
    displayPasswordReset(false)
    router.push('/account/login')
  }

  return (
    <form {...{ onSubmit }}>
      <TypeStyle.HeadlineSmall asTag='h1'>{strings.resetYourPassword}</TypeStyle.HeadlineSmall>
      <Spacer height={12} />
      <TypeStyle.BodySmall>{strings.passwordResetInstructions}</TypeStyle.BodySmall>
      <Spacer height={24} />
      <ForgotPasswordError error={forgotPasswordError} />
      <Spacer height={12} />
      <FormInput
        data-testid='email-input'
        name='email'
        type='email'
        placeholder={strings.email}
        required={true}
        {...props}
      />
      <ButtonWrapper>
        {/* TODO: Replace button with anchor tag or use an actual click function. */}
        <Button onClick={() => null} block type='submit' variant='filled'>
          {strings.submit}
        </Button>
      </ButtonWrapper>
      <GrayHorizontalBar />
      <button onClick={() => displayPasswordReset(false)}>
        <DeprecatedText>{strings.cancel}</DeprecatedText>
      </button>
    </form>
  )
}

export const ForgotPassword = (props: IProps) => {
  const translations = useTranslation()
  const FormikForgotPasswordForm = withFormik<IProps, ForgotPasswordProps>({
    mapPropsToValues: () => ({ email: '' }),
    validationSchema: () => {
      return Yup.object().shape({
        email: Yup.string().email().required(translations.magnolia.general.emailWarning),
      })
    },
    handleSubmit: () => {},
  })(ForgotPasswordForm)
  return <FormikForgotPasswordForm {...props} />
}
